// MaturaLinearExtremeInequalityClosedTask.js
// Zadanie ZAMKNIĘTE (1 pkt): nierówność liniowa z ułamkiem i skrajną liczbą całkowitą.
// Wersja “CKE-like”: wybór A–D.
//
// Kanonicznie liczymy z nierówności:
//   P - (Q - R x)/D  ⧣  T x,  gdzie ⧣ to ≥ lub ≤.
//
// Wymuszenia UX:
// - Q nie może być 0 (żeby nie było "0 - 3x").
// - nie pokazujemy współczynnika 1 przy x (x zamiast 1x).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

/* ===== helpers ===== */

const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// ładny ułamek (lub liczba całkowita)
function fracLatex(num, den) {
  const { num: N0, den: D } = simplifyFrac(num, den);
  if (D === 1) return String(N0);
  if (N0 === 0) return "0";
  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0);
  return `${sign}\\dfrac{${N}}{${D}}`;
}

// liczba mieszana jeśli się da (np. 26/3 -> 8 2/3)
function mixedFracLatex(num, den) {
  const { num: N0, den: D } = simplifyFrac(num, den);
  if (D === 1) return String(N0);

  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0);
  const whole = Math.floor(N / D);
  const rest = N % D;

  if (whole === 0 || rest === 0) return `${sign}\\dfrac{${N}}{${D}}`;
  return `${sign}${whole}\\dfrac{${rest}}{${D}}`;
}

function termCoeffXLatex(k) {
  if (k === 0) return "0";
  if (k === 1) return "x";
  if (k === -1) return "-x";
  return `${k}x`;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ===== generator ===== */

function createJune2025OriginalTask() {
  return {
    type: "MaturaLinearExtremeInequalityClosedTask",
    data: {
      P: 8,
      Q: 1,
      R: 2,
      D: 2,
      T: 3,
      opKind: "ge",
      denomRaw: 4,
      kNum: 15,
      kDen: 4,
      kVal: 15 / 4,
      finalLess: true,
      direction: "max",
      bestInteger: 3,
      swapSides: false,
      inequalityLatex: "8 - \\dfrac{1 - 2x}{2} \\ge 3x",
      ask:
        "Największa liczba całkowita, która spełnia tę nierówność, jest równa:",
      options: [1, 2, 3, 4],
      correctIndex: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 6,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_CZE") {
    return createJune2025OriginalTask();
  }

  while (true) {
    const D = pick([2, 3, 4]);

    // P sensowne, Q bez zera
    const P = pick([4, 5, 6, 7, 8, 9, 10]);
    const Q = pick([1, 2, 3, 4, 5, 6, 7, 8]); // ✅ bez 0

    // R może być 1 (wtedy wyświetlamy x zamiast 1x)
    const R = pick([1, 2, 3, 4, 5, 6]);
    const T = pick([1, 2, 3, 4, 5]);

    // ≥ lub ≤
    const opKind = pick(["ge", "le"]);
    const opLatex = opKind === "ge" ? "\\ge" : "\\le";
    const opLatexSwapped = opKind === "ge" ? "\\le" : "\\ge";

    const denomRaw = D * T - R;
    if (denomRaw === 0) continue;

    const numRaw = D * P - Q;
    const { num: kNum, den: kDen } = simplifyFrac(numRaw, denomRaw);
    const kVal = kNum / kDen;

    if (!Number.isFinite(kVal)) continue;
    if (Math.abs(kVal) > 30) continue;
    if (kDen > 12) continue;

    // nie chcemy k całkowitego
    if (Number.isInteger(kVal)) continue;

    // ge: DP - Q ≥ (DT - R)x → x ≤ k dla denom>0, x ≥ k dla denom<0
    // le: DP - Q ≤ (DT - R)x → x ≥ k dla denom>0, x ≤ k dla denom<0
    const denomPos = denomRaw > 0;
    const finalLess =
      (opKind === "ge" && denomPos) || (opKind === "le" && !denomPos);

    const direction = finalLess ? "max" : "min";
    const bestInteger = direction === "max" ? Math.floor(kVal) : Math.ceil(kVal);

    if (!Number.isFinite(bestInteger)) continue;
    if (Math.abs(bestInteger) > 30) continue;

    const swapSides = Math.random() < 0.5;

    const Rx = termCoeffXLatex(R);
    const Tx = termCoeffXLatex(T);

    let inequalityLatex;
    if (!swapSides) {
      // P - (Q - R x)/D ⧣ T x
      inequalityLatex = `${P} - \\dfrac{${Q} - ${Rx}}{${D}} ${opLatex} ${Tx}`;
    } else {
      // T x ⧣' P - (Q - R x)/D
      inequalityLatex = `${Tx} ${opLatexSwapped} ${P} - \\dfrac{${Q} - ${Rx}}{${D}}`;
    }

    // A–D: dystraktory blisko
    const distractors = new Set([bestInteger]);
    const deltas = shuffle([-2, -1, 1, 2, 3, -3]);
    for (const d of deltas) {
      if (distractors.size >= 4) break;
      distractors.add(bestInteger + d);
    }
    if (distractors.size < 4) continue;

    // ✅ odpowiedzi rosnąco (A najmniejsze, D największe)
    const options = shuffle(Array.from(distractors).slice(0, 4)).sort((a, b) => a - b);
    const correctIndex = options.indexOf(bestInteger);

    const ask =
      direction === "max"
        ? "Największa liczba całkowita, która spełnia tę nierówność, jest równa:"
        : "Najmniejsza liczba całkowita, która spełnia tę nierówność, jest równa:";

    return {
      type: "MaturaLinearExtremeInequalityClosedTask",
      data: {
        // do wyjaśnień (kroki)
        P,
        Q,
        R,
        D,
        T,
        opKind,
        denomRaw,
        kNum,
        kDen,
        kVal,
        finalLess,
        direction,
        bestInteger,
        swapSides,

        // do treści
        inequalityLatex,
        ask,

        // A–D
        options, // [a,b,c,d]
        correctIndex,
      },
    };
  }
}

/* ===== treść zadania ===== */

export function renderTask({ data }) {
  const { inequalityLatex, ask, options } = data;

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p style={{ marginTop: 8 }}>
        Dokończ zadanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      {/* ✅ W TEJ SAMEJ LINII */}
      <p style={{ marginTop: 10, marginBottom: 10 }}>
        Dana jest nierówność <InlineMath math={inequalityLatex} />.
      </p>

      <p style={{ marginTop: 8 }}>{ask}</p>

      <div style={{ marginTop: 8 }}>
        {options.map((v, i) => (
          <div key={LETTERS[i]} style={{ marginBottom: 4 }}>
            <InlineMath math={`${LETTERS[i]}.\\; ${v}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===== input ABCD ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const label = LETTERS[idx];
    onChange?.({
      choiceIndex: idx,
      label,
      value: data.options[idx],
    });
  };

  const letterOptions = LETTERS.map((L, idx) => ({
    value: idx,
    label: L,
    textNode: L,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ===== sprawdzanie ===== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

/* ===== odpowiedź ===== */

export function renderAnswer({ data }) {
  // ✅ Bez "Poprawna odpowiedź:" – runner już to pokazuje
  // ✅ InlineMath – spójna czcionka matematyczna
  const correctLetter = LETTERS[data.correctIndex];
  const correctValue = data.options[data.correctIndex];

  return <InlineMath math={`${correctLetter}.\\,${correctValue}`} />;
}

/* ===== userAnswer ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return <i>(brak odpowiedzi)</i>;
  }

  const L = LETTERS[userAnswer.choiceIndex] || "?";

  if (typeof userAnswer.value === "number") {
    // ✅ spójnie z treścią: C. 5
    return <InlineMath math={`${L}.\\; ${userAnswer.value}`} />;
  }

  return <InlineMath math={`${L}`} />;
}

/* ===== wyjaśnienie krok po kroku ===== */

export function renderExplanation({ data }) {
  const {
    P,
    Q,
    R,
    D,
    T,
    opKind,
    kNum,
    kDen,
    finalLess,
    direction,
    bestInteger,
    inequalityLatex,
  } = data;

  const opLatex = opKind === "ge" ? "\\ge" : "\\le";
  const opLatexSwapped = opKind === "ge" ? "\\le" : "\\ge";

  const DP = D * P;
  const DT = D * T;

  const startsCanonical = String(inequalityLatex).trim().startsWith(
    `${P} - \\dfrac{`
  );

  const Rx = termCoeffXLatex(R);
  const Tx = termCoeffXLatex(T);
  const DTx = termCoeffXLatex(DT);

  let leftCoeff;
  let rightConst;
  let workingRelLatex;

  if (startsCanonical) {
    // P - (Q - Rx)/D  ⧣  Tx
    // DP - Q + Rx     ⧣  DTx
    // Rx - DTx        ⧣  Q - DP
    leftCoeff = R - DT;
    rightConst = Q - DP;
    workingRelLatex = opLatex;
  } else {
    // Tx  ⧣'  P - (Q - Rx)/D
    // DTx ⧣'  DP - Q + Rx
    // DTx - Rx ⧣' DP - Q
    leftCoeff = DT - R;
    rightConst = DP - Q;
    workingRelLatex = opLatexSwapped;
  }

  const leftCoeffLatex = termCoeffXLatex(leftCoeff);
  const kFrac = fracLatex(kNum, kDen);

  let rawNum = rightConst;
  let rawDen = leftCoeff;
  if (rawDen < 0) {
    rawNum = -rawNum;
    rawDen = -rawDen;
  }
  const rawFracLatex = `\\dfrac{${rawNum}}{${rawDen}}`;

  function gcdLocal(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) {
      const t = a % b;
      a = b;
      b = t;
    }
    return a || 1;
  }

  const showsReduction = gcdLocal(rawNum, rawDen) > 1;

  const relLatexFinal = finalLess ? `x\\le ${kFrac}` : `x\\ge ${kFrac}`;
  const relLatexRaw = finalLess ? `x\\le ${rawFracLatex}` : `x\\ge ${rawFracLatex}`;

  const intervalLatex = finalLess
    ? `x\\in (-\\infty,\\; ${kFrac}]`
    : `x\\in [${kFrac},\\; +\\infty)`;

  const which = direction === "max" ? "największą" : "najmniejszą";
  const kMixed = mixedFracLatex(kNum, kDen);
  const comparison =
    direction === "max"
      ? `${bestInteger} < ${kMixed}`
      : `${bestInteger} > ${kMixed}`;

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Mnożymy obie strony przez dodatnią liczbę{" "}
        <InlineMath math={String(D)} /> (znak nierówności się nie zmienia) i
        porządkujemy tak, aby otrzymać nierówność z samym{" "}
        <InlineMath math={"x"} />. Następnie (jeśli się da) skracamy ułamek w
        progu.
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={inequalityLatex} />

        {startsCanonical ? (
          <>
            <BlockMath
              math={`${D}\\left(${P} - \\dfrac{${Q} - ${Rx}}{${D}}\\right) ${opLatex} ${D}\\cdot ${Tx}`}
            />
            <BlockMath math={`${DP} - (${Q} - ${Rx}) ${opLatex} ${DTx}`} />
            <BlockMath math={`${DP} - ${Q} + ${Rx} ${opLatex} ${DTx}`} />
            <BlockMath math={`${Rx} - ${DTx} ${opLatex} ${Q - DP}`} />
            <BlockMath math={`${leftCoeffLatex} ${workingRelLatex} ${rightConst}`} />
          </>
        ) : (
          <>
            <BlockMath
              math={`${D}\\cdot ${Tx} ${opLatexSwapped} ${D}\\left(${P} - \\dfrac{${Q} - ${Rx}}{${D}}\\right)`}
            />
            <BlockMath math={`${DTx} ${opLatexSwapped} ${DP} - (${Q} - ${Rx})`} />
            <BlockMath math={`${DTx} ${opLatexSwapped} ${DP} - ${Q} + ${Rx}`} />
            <BlockMath math={`${DTx} - ${Rx} ${opLatexSwapped} ${DP - Q}`} />
            <BlockMath math={`${leftCoeffLatex} ${workingRelLatex} ${rightConst}`} />
          </>
        )}

        {showsReduction ? (
          <>
            <BlockMath math={relLatexRaw} />
            <BlockMath math={relLatexFinal} />
          </>
        ) : (
          <BlockMath math={relLatexFinal} />
        )}
      </div>

      <p>
        <strong>Krok 2.</strong> Zapisujemy rozwiązanie w postaci przedziału:
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath math={intervalLatex} />
      </div>

      <p>
        <strong>Krok 3.</strong> Wybieramy {which} liczbę całkowitą spełniającą
        nierówność.
      </p>

      <p style={{ marginTop: 6 }}>
        Z przedziału widać, że {which} liczba całkowita spełniająca nierówność
        to <InlineMath math={String(bestInteger)} /> (bo{" "}
        <InlineMath math={comparison} />).
      </p>
    </div>
  );
}
